import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Message from "../components/LoadingError/Error";
import {
  addToCart,
  removefromcart,
  clearCartItems,
} from "../Redux/Actions/cartActions";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import {
  getPublicStoreSettings,
  getNextWhatsappNumber,
} from "../Redux/Actions/storeSettingActions";
import "./styles/checkout.css";

const formatCOP = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const SummaryCartScreen = ({ match, location }) => {
  const dispatch = useDispatch();
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);

  const productId = match?.params?.id;
  const qty = location?.search ? Number(location.search.split("=")[1]) : 1;

  const { cartItems = [] } = useSelector((state) => state.cart || {});
  const { settings = {} } = useSelector(
    (state) => state.publicStoreSettings || {}
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(getPublicStoreSettings());
  }, [dispatch]);

  useEffect(() => {
    const shouldClearCart = sessionStorage.getItem("pendingCartClear");

    if (shouldClearCart === "true") {
      dispatch(clearCartItems());
      sessionStorage.removeItem("pendingCartClear");
    }
  }, [dispatch]);

  useEffect(() => {
    if (productId) dispatch(addToCart(productId, qty));
  }, [dispatch, productId, qty]);

  const removeFromCartHandle = (variantKey) => {
    dispatch(removefromcart(variantKey));
  };

  const prices = useMemo(() => {
    const itemsPrice = cartItems.reduce(
      (acc, item) => acc + Number(item.price || 0) * Number(item.qty || 0),
      0
    );

    return {
      itemsPrice,
      totalPrice: itemsPrice,
    };
  }, [cartItems]);

  const handleSendWhatsApp = async () => {
    if (!cartItems.length || sendingWhatsapp) return;

    setSendingWhatsapp(true);

    try {
      const buttonWasClickedAt = Date.now();

      let whatsappNumber = "";

      try {
        whatsappNumber = await getNextWhatsappNumber();
      } catch (error) {
        alert(
          error.response?.data?.message ||
            "No hay un número de WhatsApp configurado en este momento."
        );
        return;
      }

      if (!whatsappNumber) {
        alert("No hay un número de WhatsApp configurado en este momento.");
        return;
      }

      const itemsBlock = cartItems
        .map((item, index) => {
          const lines = [`*${index + 1}. ${item.name}*`];

          if (item.size) lines.push(`Talla: ${item.size}`);
          if (item.color) lines.push(`Color: ${item.color}`);

          lines.push(`Cantidad: ${item.qty}`);
          lines.push(`Precio unitario: $${formatCOP(item.price)}`);
          lines.push(
            `Subtotal: $${formatCOP(
              Number(item.qty || 0) * Number(item.price || 0)
            )}`
          );

          return lines.join("\n");
        })
        .join("\n\n——————————————\n\n");

      const header =
        settings?.whatsappMessageHeader ||
        "Hola, deseo solicitar la siguiente cotización:";

      const message = [
        header,
        "",
        "*Resumen del pedido*",
        itemsBlock,
        "",
        "——————————————",
        `*Total:* $${formatCOP(prices.totalPrice)}`,
        "",
        "Quedo atento(a) para confirmar disponibilidad y proceso de pago.",
      ].join("\n");

      const whatsappUrl = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent(
        message
      )}`;

      sessionStorage.setItem("pendingCartClear", "true");

      window.location.href = whatsappUrl;

      setTimeout(() => {
        if (Date.now() - buttonWasClickedAt > 1000) {
          dispatch(clearCartItems());
          sessionStorage.removeItem("pendingCartClear");
        }
      }, 1500);
    } finally {
      setTimeout(() => {
        setSendingWhatsapp(false);
      }, 2000);
    }
  };

  return (
    <>
      <Header />

      <div className="container my-4">
        <div className="row g-4">
          <div className="col-lg-7">
            <div className="card shadow-sm border-0 checkout-card">
              <div className="card-body p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Tu carrito</h5>
                  <small className="text-muted">
                    ({cartItems.length}) productos
                  </small>
                </div>

                {cartItems.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    Tu carrito está vacío. <Link to="/">Ir a comprar</Link>
                  </div>
                ) : (
                  <>
                    {cartItems.map((item) => (
                      <div
                        className="checkout-item"
                        key={
                          item.variantKey ||
                          `${item.product}-${item.size || ""}-${
                            item.color || ""
                          }`
                        }
                      >
                        <div className="checkout-item-row">
                          <div className="checkout-item-img">
                            <img src={item.image} alt={item.name} />
                          </div>

                          <div className="checkout-item-info">
                            <Link
                              to={`/products/${item.product}`}
                              className="checkout-item-name"
                            >
                              {item.name}
                            </Link>

                            <div className="checkout-item-meta">
                              {item.size && (
                                <span className="checkout-badge">
                                  Talla: {item.size}
                                </span>
                              )}

                              {item.color && (
                                <span className="checkout-badge">
                                  Color: {item.color}
                                </span>
                              )}
                            </div>

                            <small className="checkout-item-price d-block">
                              $ {formatCOP(item.price)}
                            </small>
                          </div>

                          <div className="checkout-item-actions">
                            <div className="checkout-qty-pill">
                              <span>Cantidad:</span>
                              <strong>{item.qty}</strong>
                            </div>

                            <button
                              type="button"
                              className="btn-delete-icon"
                              onClick={() =>
                                removeFromCartHandle(item.variantKey)
                              }
                              aria-label={`Eliminar ${item.name}`}
                              title="Eliminar"
                            >
                              <HugeiconsIcon
                                icon={Cancel01Icon}
                                size={18}
                                color="#fff"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="checkout-products-total">
                      <span className="text-muted">Total</span>
                      <span className="checkout-products-total-value">
                        $ {formatCOP(prices.totalPrice)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <Link to="/cart" className="checkout-continue mt-2">
              Volver al carrito
            </Link>
          </div>

          <div className="col-lg-5">
            <div className="card shadow-sm border-0 checkout-summary-card">
              <div className="card-body d-flex flex-column p-3 p-md-4">
                <h5 className="mb-3">Resumen del pedido</h5>

                {cartItems.length === 0 ? (
                  <Message variant="alert-info">Tu carrito está vacío.</Message>
                ) : (
                  <>
                    <div className="checkout-summary-box mb-3">
                      <div className="checkout-summary-row checkout-summary-total">
                        <span>Total</span>
                        <strong>$ {formatCOP(prices.totalPrice)}</strong>
                      </div>
                    </div>

                    <div className="mt-auto">
                      <button
                        type="button"
                        className="btn btn-whatsapp w-100"
                        onClick={handleSendWhatsApp}
                        disabled={sendingWhatsapp}
                      >
                        {sendingWhatsapp
                          ? "Abriendo WhatsApp..."
                          : "Realizar pedido por WhatsApp"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SummaryCartScreen; 